import logo from './logo.svg';
import './App.css';
import { useState, useEffect, useRef } from "react";
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell } from 'recharts';
import { EventSourcePolyfill } from 'event-source-polyfill';


function App() {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [activeTab, setActiveTab] = useState("upload");
  const [ehRange, setEhRange] = useState([0.2, 0.8]);
  const [logCuRange, setLogCuRange] = useState([-11, -9]);
  const [logNH4Range, setLogNH4Range] = useState([-12, -8]);

  const [resultsEnabled, setResultsEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [ws, setWs] = useState(null);
  const eventSourceRef = useRef(null);

  // State for parsed values and slider values
  const [speciesRanges, setSpeciesRanges] = useState({});
  const [speciesSliderValues, setSpeciesSliderValues] = useState({});

  // Add to state: which species are held constant
  const [speciesConstant, setSpeciesConstant] = useState({});

  // --- Add to state for Eh fix ---
  const [ehFixed, setEhFixed] = useState(false);

  // --- Add to state for reactant sliders ---
  const [reactants, setReactants] = useState({});
  const [reactantSliderValues, setReactantSliderValues] = useState({});

  // Add to state for axis selection
  const [checkedAxes, setCheckedAxes] = useState([0, 1]);

  // State for species multiples (fixed ratio relationships)
  const [speciesMultiples, setSpeciesMultiples] = useState({});
  // State for custom multiplier values for multiples
  const [multiplierValues, setMultiplierValues] = useState({});

  const parameterLabels = results[0]?.parameter_labels
    ? Object.values(results[0].parameter_labels)
    : [];
  const numDims = results[0]?.point?.length || 2;

  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [ws]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const fileObj = e.dataTransfer.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target.result;
        setFile({
          name: fileObj.name,
          content: content
        });
        setSpeciesRanges(parseReaFile(content));
      };
      reader.readAsText(fileObj);
    }
  };

  const handleFileChange = (e) => {
    const fileObj = e.target.files[0];
    if (!fileObj) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setFile({
        name: fileObj.name,
        content: content
      });
      setSpeciesRanges(parseReaFile(content));
    };
    reader.readAsText(fileObj);
  };

  // Parse .rea file: set default range [-12, -8], and set slider to parsed value if in range, else [-12, -8]
  const parseReaFile = (content) => {
    const lines = content.split('\n');
    const species = {};
    const sliderValues = {};
    const constant = {};
    const reactantsFound = {};
    const reactantSliderInit = {};

    lines.forEach(line => {
      // Trim the line to remove leading/trailing whitespace
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) return;
      
      // Parse concentrations
      const match = trimmedLine.match(/^(\S+)\s+=\s+(-?\d+(?:\.\d+)?)\s+log\s+mmol\/kg/);
      if (match) {
        const [_, speciesName, value] = match;
        const v = parseFloat(value);
        species[speciesName] = { range: [-12, -8], parsed: v };
        sliderValues[speciesName] = [v, v];
        constant[speciesName] = false;
      }
      
      // Parse pH (unitless) - more flexible matching
      const phMatch = trimmedLine.match(/^pH\s*=\s*(-?\d+(?:\.\d+)?)/i);
      if (phMatch) {
        const value = parseFloat(phMatch[1]);
        species['pH'] = { range: [0, 14], parsed: value }; // pH typically ranges from 0 to 14
        sliderValues['pH'] = [value, value];
        constant['pH'] = false;
      }
      
      // Parse temperature (in C) - more flexible matching
      const tempMatch = trimmedLine.match(/^temperature\s*=\s*(-?\d+(?:\.\d+)?)\s*C/i);
      if (tempMatch) {
        const value = parseFloat(tempMatch[1]);
        species['temperature'] = { range: [0, 100], parsed: value }; // Reasonable range for temperature in C
        sliderValues['temperature'] = [value, value];
        constant['temperature'] = false;
      }
      
      // Parse reactants
      const reactMatch = trimmedLine.match(/^react\s+([0-9.eE+-]+)\s+(\w+)\s+of\s+(.+)/) ||
                         trimmedLine.match(/^react\s+([0-9.eE+-]+)\s+(\w+)\s+(.+)/);
      if (reactMatch) {
        const amount = parseFloat(reactMatch[1]);
        const unit = reactMatch[2];
        const material = reactMatch[3].replace(/,.*$/, '').trim();
        if (["mmol", "mol", "mg"].includes(unit)) {
          reactantsFound[material] = { unit, parsed: amount };
          reactantSliderInit[material] = [amount, amount];
        }
      }
    });
    
    setSpeciesSliderValues(sliderValues);
    setSpeciesConstant(constant);
    setReactants(reactantsFound);
    setReactantSliderValues(reactantSliderInit);
    return species;
  };

  // 2. Get axis labels from the first result:
  const xLabel = results[0]?.parameter_labels?.label_1 || "X";
  const yLabel = results[0]?.parameter_labels?.label_2 || "Y";

  // Add this function to handle downloads
  const handleDownload = (expPath) => {
    if (!expPath) return;
    
    // Create URL with the path parameter
    const downloadUrl = `https://api.alkali-eng.com:5000/download?path=${encodeURIComponent(expPath)}`;
    
    // Open in new tab/window to trigger download
    try {
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Failed to open download URL:', error);
      alert('Failed to start download. Please try again.');
    }
  };

  const resultVals = results.map(r => r.result);
  const min = Math.min(...resultVals);
  const max = Math.max(...resultVals);

  // --- Helper to build bounds array for server ---
  const buildBounds = () => {
    const bounds = [];

    // Eh
    if (ehFixed) {
      bounds.push(["Eh", ehRange[0], ehRange[0]]);
    } else {
      bounds.push(["Eh", ehRange[0], ehRange[1]]);
    }

    // Concentration species
    Object.entries(speciesRanges).forEach(([species, { range }]) => {
      if (speciesConstant[species]) {
        const val = speciesSliderValues[species]?.[0] ?? range[0];
        bounds.push([species, val, val]);
      } else {
        const vals = speciesSliderValues[species] || range;
        bounds.push([species, vals[0], vals[1]]);
      }
    });

    // Reactants
    Object.entries(reactants).forEach(([material, { unit }]) => {
      const key = `${material}-react`;
      const reactVal = reactantSliderValues[material];
      const parsed = reactants[material]?.parsed ?? 0;
      const vals = reactVal?.value || [parsed, parsed];
      bounds.push([key, vals[0], vals[1]]);
    });

    return bounds;
  };

  // Helper to build multiples object for server
  const buildMultiples = () => {
    const multiples = {};
    Object.entries(speciesMultiples).forEach(([speciesA, targetSpecies]) => {
      if (targetSpecies && targetSpecies !== 'none') {
        // Use the user-entered multiplier value, default to 1 if not specified
        const multiplier = multiplierValues[speciesA] ? parseFloat(multiplierValues[speciesA]) : 1;
        multiples[speciesA] = [multiplier, targetSpecies];
      }
    });
    return multiples;
  };

  const handleRunSimulation = () => {
    // Prevent multiple clicks if already loading
    if (loading) return;

    setLoading(true);
    setResults([]);
    setResultsEnabled(true);
    setActiveTab("results");

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const requestBody = {
      rea_content: file ? file.content : "",
      original_filename: file ? file.name : "",
      target_species: "Cu",
      bounds: buildBounds(),
      n_initial: 64,
      n_iterations: 32,
      kernel_type: "matern25",
      acqf_type: "UCB",
      kappa: 5.0,
      multiples: buildMultiples()
    };

    // First send a POST to prepare the simulation and get a session ID
    fetch('https://api.alkali-eng.com:5000/prepare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody)
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        console.error('Preparation error:', data.error);
        setLoading(false);
        return;
      }
      
      // Step 2: Connect to SSE stream with the session ID
      const sessionId = data.session_id;
      console.log('Received session ID:', sessionId);
      
      const connectEventSource = () => {
        const eventSource = new EventSourcePolyfill(
          `https://api.alkali-eng.com:5000/stream?session_id=${sessionId}`,
          {
            heartbeatTimeout: 120000 // Set to 2 minutes (120 seconds)
          }
        );
        
        eventSource.onopen = () => {
          console.log('SSE connection opened');
        };
        
        eventSource.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.heartbeat) {
            console.log('Received heartbeat, keeping connection alive');
            return;
          }
          if (data.point && Array.isArray(data.point)) {
            setResults(prev => [
              ...prev,
              {
                phase: data.phase,
                iteration: data.iteration,
                point: data.point, // Store the full point array
                x: data.point[0], // First dimension for plot
                y: data.point[1], // Second dimension for plot
                result: data.result,
                timestamp: data.timestamp,
                parameter_labels: data.parameter_labels,
                exp_path: data.exp_path
              }
            ]);
          }
          console.log('SSE message:', event.data);
        };
        
        eventSource.onerror = (err) => {
          console.error('SSE error:', err);
          setLoading(false);
          eventSource.close();
          // Do not automatically reconnect to prevent multiple streams
          // setTimeout(connectEventSource, 5000); // Commented out to prevent multiple connections
        };
        
        // Store the eventSource reference for cleanup
        eventSourceRef.current = eventSource;
      };

      // Initial connection
      connectEventSource();
    })
    .catch(error => {
      console.error('Fetch error:', error);
      setLoading(false);
    });
  };

  // Add a new function to handle reset
  const handleReset = () => {
    setLoading(false);
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    // Optionally, you can clear results or reset other states if needed
    // setResults([]);
  };

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Helper: continuous color scale (blue → red)
  const getColor = (result, min, max) => {
    // Normalize result to [0, 1]
    const norm = (result - min) / (max - min || 1);
    // Interpolate between blue (low) and red (high)
    // Blue: rgb(41,128,185), Red: rgb(231,76,60)
    const r = Math.round(41 + (231 - 41) * norm);
    const g = Math.round(128 + (76 - 128) * norm);
    const b = Math.round(185 + (60 - 185) * norm);
    return `rgb(${r},${g},${b})`;
  };

  // Custom tooltip for ScatterChart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div style={{ background: "#fff", border: "1px solid #ccc", padding: 10 }}>
          {d.point.map((val, idx) => (
            <div key={idx}>
              <b>{d.parameter_labels[`label_${idx + 1}`]}:</b> {val.toFixed(2)}
            </div>
          ))}
          <div><b>Result:</b> {d.result}</div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8f5f2] via-[#f3ede7] to-[#e6d3c2]">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full relative border border-[#e0bfa3]">
        <div className="flex w-full rounded-t-2xl bg-white z-10 shadow-sm">
          <button
            className={`flex-1 px-4 py-4 rounded-t-2xl text-lg font-semibold transition-colors
              ${activeTab === "upload"
                ? "bg-[#fff7f0] text-[#B87333] shadow border-b-2 border-b-[#B87333]"
                : "bg-[#f8f5f2] text-[#a6a6a6]"
              }`}
            onClick={() => setActiveTab("upload")}
          >
            Upload
          </button>
          <button
            className={`flex-1 px-4 py-4 rounded-t-2xl text-lg font-semibold transition-colors
              ${activeTab === "parameters"
                ? "bg-[#fff7f0] text-[#B87333] shadow border-b-2 border-b-[#B87333]"
                : "bg-[#f8f5f2] text-[#a6a6a6]"
              }`}
            onClick={() => setActiveTab("parameters")}
          >
            Parameters
          </button>
          <button
            className={`flex-1 px-4 py-4 rounded-t-2xl text-lg font-semibold transition-colors
              ${activeTab === "results"
                ? "bg-[#fff7f0] text-[#B87333] shadow border-b-2 border-b-[#B87333]"
                : resultsEnabled
                ? "bg-[#f8f5f2] text-[#a6a6a6]"
                : "bg-[#f8f5f2] text-[#a6a6a6] cursor-not-allowed"
              }`}
            onClick={() => resultsEnabled && setActiveTab("results")}
            disabled={!resultsEnabled}
          >
            Results
          </button>
        </div>
        <div className="p-12 pt-16 min-h-screen flex flex-col justify-between">
          {activeTab === "upload" && (
            <form className="space-y-8">
              <div>
                <label className="block font-semibold mb-2 text-gray-700 text-lg">Reaction File (.rea)</label>
                <div
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-200
                    ${dragActive ? "border-[#B87333] bg-[#F5E9DA]" : "border-[#E6C3A5] bg-[#FFF7F0]"}
                  `}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    accept=".rea"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer"
                    style={{ color: '#B87333' }}
                  >
                    {file ? file.name : "Drag and drop your .rea file here, or click to browse"}
                  </label>
                </div>
              </div>
              <div>
                <label className="block font-semibold mb-2 text-gray-700">Target Species</label>
                <select className="w-full border border-[#E6C3A5] rounded-lg px-4 py-3 bg-[#FFF7F0] text-[#B87333] focus:ring-2 focus:ring-[#B87333] focus:border-[#B87333] transition">
                  <option value="Cu">Copper (Cu)</option>
                </select>
              </div>
              <div className="flex justify-between">
                <button
                  type="button"
                  className="px-6 py-3 border rounded-lg bg-[#FFF7F0] text-[#B87333] border-[#B87333] hover:bg-[#F5E9DA] transition font-medium"
                  onClick={() => setFile(null)}
                >
                  Reset
                </button>
              </div>
            </form>
          )}
          {activeTab === "parameters" && (
            <div className="space-y-8">
              <h2 className="text-2xl font-bold mb-4 text-[#B87333]">Simulation Setup</h2>
              <div>
                <label className="block font-semibold mb-2 text-gray-700">Eh (V)</label>
                <div className="flex items-center gap-4 px-2">
                  {ehFixed ? (
                    <Slider
                      min={0}
                      max={1}
                      step={0.01}
                      value={ehRange[0]}
                      onChange={val => setEhRange([val, val])}
                      trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                      handleStyle={[
                        { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                      ]}
                      railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                    />
                  ) : (
                    <Slider
                      range
                      min={0}
                      max={1}
                      step={0.01}
                      value={ehRange}
                      onChange={setEhRange}
                      trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                      handleStyle={[
                        { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 },
                        { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                      ]}
                      railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                    />
                  )}
                  <label className="flex items-center gap-1 text-xs text-[#B87333] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={ehFixed}
                      onChange={e => {
                        const checked = e.target.checked;
                        setEhFixed(checked);
                        if (checked) setEhRange([0.5, 0.5]);
                      }}
                      className="accent-[#B87333]"
                    />
                    Fix
                  </label>
                </div>
                <div className="text-sm" style={{ color: '#B87333' }}>
                  {ehFixed
                    ? `${ehRange[0].toFixed(2)} V (fixed)`
                    : `${ehRange[0].toFixed(2)} V — ${ehRange[1].toFixed(2)} V`
                  }
                </div>
              </div>
              <div className="space-y-4">
                {Object.entries(speciesRanges).map(([species, { range, parsed }]) => (
                  <div key={species} className="flex items-center min-h-[60px]">
                    <div className="w-40 font-semibold text-gray-700 text-right pr-4 text-base">
                      {species === "pH" ? "pH" : species === "temperature" ? "Temperature (C)" : `log ${species} (mmol/kg)`}
                    </div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex items-center gap-4">
                        {speciesConstant[species] ? (
                          <Slider
                            min={range[0]}
                            max={range[1]}
                            step={0.01}
                            value={speciesSliderValues[species]?.[0] ?? -10}
                            onChange={val => {
                              setSpeciesSliderValues(prev => ({
                                ...prev,
                                [species]: [val, val]
                              }));
                            }}
                            trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                            handleStyle={[
                              { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                            ]}
                            railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                          />
                        ) : (
                          <Slider
                            range
                            min={range[0]}
                            max={range[1]}
                            step={0.01}
                            value={speciesSliderValues[species] || range}
                            onChange={newRange => {
                              setSpeciesSliderValues(prev => ({
                                ...prev,
                                [species]: newRange
                              }));
                            }}
                            trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                            handleStyle={[
                              { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 },
                              { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                            ]}
                            railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                          />
                        )}
                        <label className="flex items-center gap-1 text-xs text-[#B87333] cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={speciesConstant[species] || false}
                            onChange={e => {
                              const checked = e.target.checked;
                              setSpeciesConstant(prev => ({
                                ...prev,
                                [species]: checked
                              }));
                              if (checked) {
                                setSpeciesSliderValues(prev => {
                                  const current = prev[species] || range;
                                  return {
                                    ...prev,
                                    [species]: [current[0], current[0]]
                                  };
                                });
                              }
                            }}
                            className="accent-[#B87333]"
                          />
                          Fix
                        </label>
                        <select
                          value={speciesMultiples[species] || 'none'}
                          onChange={e => {
                            const targetSpecies = e.target.value;
                            setSpeciesMultiples(prev => ({
                              ...prev,
                              [species]: targetSpecies
                            }));
                          }}
                          className="text-xs border border-[#E6C3A5] rounded px-2 py-1 bg-[#FFF7F0] text-[#B87333]"
                        >
                          <option value="none">None</option>
                          {Object.keys(speciesRanges).filter(s => s !== species).map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                          {Object.keys(reactants).map(r => (
                            <option key={`${r}-react`} value={`${r}-react`}>{r} (react)</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          step="0.1"
                          value={multiplierValues[species] || ''}
                          onChange={e => {
                            const value = e.target.value;
                            setMultiplierValues(prev => ({
                              ...prev,
                              [species]: value
                            }));
                          }}
                          placeholder="1.0"
                          className="w-16 text-xs border border-[#E6C3A5] rounded px-2 py-1 bg-[#FFF7F0] text-[#B87333]"
                          disabled={speciesMultiples[species] === 'none' || !speciesMultiples[species]}
                        />
                      </div>
                      <div className="text-sm mt-1" style={{ color: '#B87333' }}>
                        {speciesConstant[species]
                          ? `Fixed: ${(speciesSliderValues[species]?.[0] ?? -10).toFixed(2)} ${species === "temperature" ? "C" : species === "pH" ? "" : "log mmol/kg"}`
                          : `${(speciesSliderValues[species]?.[0] ?? range[0]).toFixed(2)} — ${(speciesSliderValues[species]?.[1] ?? range[1]).toFixed(2)} ${species === "temperature" ? "C" : species === "pH" ? "" : "log mmol/kg"}`
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {Object.keys(reactants).length > 0 && (
                <div className="mt-12">
                  <h2 className="text-xl font-bold mb-4 text-[#B87333]">Reactant Amounts</h2>
                  <div className="space-y-4">
                    {Object.entries(reactants).map(([material, { unit, parsed }]) => {
                      const isFixed = reactantSliderValues[material]?.fixed || false;
                      const value = reactantSliderValues[material]?.value || [Math.max(0, Math.min(parsed ?? 0, 5)), Math.max(0, Math.min(parsed ?? 0, 5))];
                      const fixedValue = isFixed ? value[0] : value;
                      return (
                        <div key={material} className="flex items-center min-h-[60px]">
                          <div className="w-40 font-semibold text-gray-700 text-right pr-4 text-base">
                            {material} ({unit})
                          </div>
                          <div className="flex-1 flex flex-col">
                            <div className="flex items-center gap-4">
                              {isFixed ? (
                                <Slider
                                  min={0}
                                  max={5}
                                  step={0.01}
                                  value={fixedValue}
                                  onChange={val => {
                                    setReactantSliderValues(prev => ({
                                      ...prev,
                                      [material]: { value: [val, val], fixed: true }
                                    }));
                                  }}
                                  trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                                  handleStyle={[
                                    { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                                  ]}
                                  railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                                />
                              ) : (
                                <Slider
                                  range
                                  min={0}
                                  max={5}
                                  step={0.01}
                                  value={value}
                                  onChange={newRange => {
                                    setReactantSliderValues(prev => ({
                                      ...prev,
                                      [material]: { value: newRange, fixed: false }
                                    }));
                                  }}
                                  trackStyle={[{ backgroundColor: '#B87333', height: 6 }]}
                                  handleStyle={[
                                    { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 },
                                    { borderColor: '#B87333', backgroundColor: '#fff', height: 24, width: 24, marginTop: -9 }
                                  ]}
                                  railStyle={{ backgroundColor: '#E6C3A5', height: 6 }}
                                />
                              )}
                              <label className="flex items-center gap-1 text-xs text-[#B87333] cursor-pointer select-none">
                                <input
                                  type="checkbox"
                                  checked={isFixed}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setReactantSliderValues(prev => {
                                      const current = prev[material]?.value || [parsed ?? 0, parsed ?? 0];
                                      return {
                                        ...prev,
                                        [material]: checked
                                          ? { value: [current[0], current[0]], fixed: true }
                                          : { value: current, fixed: false }
                                      };
                                    });
                                  }}
                                  className="accent-[#B87333]"
                                />
                                Fix
                              </label>
                              <select
                                value={speciesMultiples[`${material}-react`] || 'none'}
                                onChange={e => {
                                  const targetSpecies = e.target.value;
                                  setSpeciesMultiples(prev => ({
                                    ...prev,
                                    [`${material}-react`]: targetSpecies
                                  }));
                                }}
                                className="text-xs border border-[#E6C3A5] rounded px-2 py-1 bg-[#FFF7F0] text-[#B87333]"
                              >
                                <option value="none">None</option>
                                {Object.keys(speciesRanges).map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                                {Object.keys(reactants).filter(r => r !== material).map(r => (
                                  <option key={`${r}-react`} value={`${r}-react`}>{r} (react)</option>
                                ))}
                              </select>
                              <input
                                type="number"
                                step="0.1"
                                value={multiplierValues[`${material}-react`] || ''}
                                onChange={e => {
                                  const value = e.target.value;
                                  setMultiplierValues(prev => ({
                                    ...prev,
                                    [`${material}-react`]: value
                                  }));
                                }}
                                placeholder="1.0"
                                className="w-16 text-xs border border-[#E6C3A5] rounded px-2 py-1 bg-[#FFF7F0] text-[#B87333]"
                                disabled={speciesMultiples[`${material}-react`] === 'none' || !speciesMultiples[`${material}-react`]}
                              />
                            </div>
                            <div className="text-sm mt-1" style={{ color: '#B87333' }}>
                              {isFixed
                                ? `Fixed: ${(value[0] ?? 0).toFixed(2)} ${unit}`
                                : `${(value[0] ?? 0).toFixed(2)} — ${(value[1] ?? 0).toFixed(2)} ${unit}`
                              }
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <button
                  type="button"
                  className="px-6 py-3 border rounded-lg bg-[#FFF7F0] text-[#B87333] border-[#B87333] hover:bg-[#F5E9DA] transition font-medium"
                  onClick={() => setActiveTab("upload")}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="px-8 py-3 rounded-lg text-white bg-[#B87333] hover:bg-[#a05d27] transition font-semibold shadow"
                  onClick={handleRunSimulation}
                  disabled={loading}
                >
                  {loading ? "Running..." : "Run Simulation"}
                </button>
              </div>
            </div>
          )}
          {activeTab === "results" && (
            <div className="flex flex-col h-full">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-[#B87333]">Simulation Results</h2>
                <button
                  type="button"
                  className="px-6 py-3 border rounded-lg bg-[#FFF7F0] text-[#B87333] border-[#B87333] hover:bg-[#F5E9DA] transition font-medium"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
              {loading && (
                <div className="flex items-center justify-center text-[#B87333] mb-4 gap-2">
                  <svg className="animate-spin h-5 w-5 text-[#B87333]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                  </svg>
                  <span>Running simulation...</span>
                </div>
              )}
              <div className="flex-1 overflow-auto">
                <div className="flex flex-row gap-8">
                  {/* Axis selectors */}
                  <div className="flex flex-col items-start gap-2">
                    <label className="font-semibold text-[#B87333] mb-2">Select 2 axes to plot:</label>
                    {parameterLabels.map((label, idx) => (
                      <label key={idx} className="flex items-center gap-2 text-[#B87333]">
                        <input
                          type="checkbox"
                          checked={checkedAxes.includes(idx)}
                          onChange={e => {
                            if (e.target.checked) {
                              if (checkedAxes.length < 2) setCheckedAxes([...checkedAxes, idx]);
                            } else {
                              setCheckedAxes(checkedAxes.filter(i => i !== idx));
                            }
                          }}
                          disabled={
                            !checkedAxes.includes(idx) && checkedAxes.length >= 2
                          }
                          className="accent-[#B87333]"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {/* Plot */}
                  <div>
                    {checkedAxes.length === 2 ? (
                      <div style={{ position: "relative", width: 600, height: 600 }}>
                        <ScatterChart
                          width={600}
                          height={600}
                          margin={{ top: 40, right: 40, left: 40, bottom: 40 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            type="number"
                            dataKey={d => d.point?.[checkedAxes[0]]}
                            name={parameterLabels[checkedAxes[0]] || "X"}
                            label={{ value: parameterLabels[checkedAxes[0]] || "X", position: "insideBottom", offset: -10, style: { fontSize: 18, fill: "#B87333" } }}
                            tick={{ fontSize: 16, fill: "#222" }}
                            axisLine={{ stroke: "#222", strokeWidth: 1 }}
                            domain={['auto', 'auto']}
                          />
                          <YAxis
                            type="number"
                            dataKey={d => d.point?.[checkedAxes[1]]}
                            name={parameterLabels[checkedAxes[1]] || "Y"}
                            label={{
                              value: parameterLabels[checkedAxes[1]] || "Y",
                              angle: -90,
                              position: "center",
                              style: { fontSize: 18, fill: "#B87333" }
                            }}
                            tick={{ fontSize: 16, fill: "#222" }}
                            axisLine={{ stroke: "#222", strokeWidth: 1 }}
                            domain={['auto', 'auto']}
                          />
                          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                          <Scatter
                            name="Result"
                            data={results}
                            shape={(props) => {
                              const { payload, cx, cy } = props;
                              if (payload.result < 0) {
                                // Draw a smaller star shape for negative results (non-convergence)
                                return (
                                  <svg x={cx - 7} y={cy - 7} width={14} height={14} viewBox="0 0 24 24">
                                    <polygon
                                      points="12,2 15,9 22,9.5 17,14.5 18.5,22 12,18 5.5,22 7,14.5 2,9.5 9,9"
                                      fill="#222"
                                      stroke="#222"
                                      strokeWidth="1"
                                    />
                                  </svg>
                                );
                              }
                              // Default circle for non-negative results
                              return (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={7}
                                  fill={getColor(payload.result, min, max)}
                                  stroke="#fff"
                                  strokeWidth={1.5}
                                />
                              );
                            }}
                          >
                          </Scatter>
                          {/* Color legend (color bar) */}
                          <Legend
                            verticalAlign="top"
                            align="right"
                            payload={[]}
                            wrapperStyle={{ top: 10, right: 40, fontSize: 16 }}
                          />
                        </ScatterChart>
                        {/* Heat legend (color bar) */}
                        <div
                          style={{
                            position: "absolute",
                            top: 80,
                            right: -36,
                            height: 400,
                            width: 36,
                            background: `linear-gradient(to bottom, ${getColor(max, min, max)}, ${getColor(min, min, max)})`,
                            borderRadius: 8,
                            border: "1px solid #e0bfa3",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 10,
                          }}
                        >
                          {/* Max label above the bar */}
                          <span style={{ color: "#222", fontSize: 16, marginBottom: 4, position: "absolute", top: -24, left: "50%", transform: "translateX(-50%)" }}>
                            {max.toFixed(2)}
                          </span>
                          {/* Min label below the bar */}
                          <span style={{ color: "#222", fontSize: 16, marginTop: 4, position: "absolute", bottom: -24, left: "50%", transform: "translateX(-50%)" }}>
                            {min.toFixed(2)}
                          </span>
                          {/* Centered label */}
                          <span
                            style={{
                              color: "#222",
                              fontSize: 16,
                              writingMode: "vertical-rl",
                              transform: "rotate(180deg)",
                              margin: "8px 0",
                              zIndex: 1,
                            }}
                          >
                            Cu Concentration (μmol/kg)
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-[#B87333] font-semibold mt-8">
                        {checkedAxes.length < 2
                          ? "Please select 2 axes to plot."
                          : "Please select only 2 axes."}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {results.map((result, index) => {
                    const formatNum = (val) => {
                      if (typeof val !== 'number' || isNaN(val)) return val;
                      if (Number(val.toFixed(2)) === -0.10) return "Non-convergence";
                      return val.toFixed(2);
                    };
                    return (
                      <div
                        key={index}
                        className="rounded-xl shadow border border-[#e0bfa3] overflow-hidden flex flex-col justify-between relative group p-3 min-h-[170px]"
                        style={{
                          background: '#f5e9da',
                          color: '#222',
                          minWidth: 0,
                        }}
                      >
                        <div>
                          <div className={`text-xs font-bold uppercase tracking-widest mb-1 ${
                            result.phase.toLowerCase() === 'sobol' ? 'text-[#B87333]' : 'text-[#4B9C8E]'
                          }`}>
                            Phase: {result.phase.toLowerCase() === 'bo' ? 'Bayesian' : result.phase}
                          </div>
                          <div className="text-base font-bold mb-1">Experiment {result.iteration}</div>
                          {result.point.map((val, idx) => (
                            <div key={idx} className="text-sm mb-1">
                              <span className="font-semibold">{result.parameter_labels[`label_${idx + 1}`]}:</span> {formatNum(val)}
                            </div>
                          ))}
                          <div className="text-sm font-bold mb-1">
                            {formatNum(result.result)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs opacity-70 mb-2 break-all">{result.timestamp}</div>
                          <button
                            onClick={() => handleDownload(result.exp_path)}
                            className="w-full px-2 py-1 bg-[#B87333] text-white rounded font-semibold shadow hover:bg-[#a05d27] transition text-sm"
                          >
                            Download
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
